import { List } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { BusinessListItem } from 'components/molecules/BusinessListItem';
import { Business } from 'models/Business';
import { Division } from 'models/Division';
import { useMemo } from 'react';
import { useGetBusinessesQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useGetCatalogConfigsQuery } from 'redux/services/rudyCadabby/rudyCadabbyApi';
import { useAppSelector } from 'redux/store';

export const BusinessList = (): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { inheritanceTypeFilter, customerSearch } = useAppSelector((state) => state.businessList);

  const { data: businesses, isLoading: isBusinessLoading, isFetching } = useGetBusinessesQuery({ includeDivisions: true });

  const { data: catalogConfigData, isLoading: isCatalogConfigLoading, isFetching: isConfigFetching } = useGetCatalogConfigsQuery({ dataInheritanceLevels: inheritanceTypeFilter.join(',') });

  const isDocumentLoading = Boolean(isBusinessLoading || isCatalogConfigLoading || isFetching || isConfigFetching);

  /* ******************** Functions / Variables ******************** */
  const filteredData = useMemo(() => {
    let returnData: Business[] = [];

    // If business and divisions loaded
    if (businesses?.data && catalogConfigData?.data) {
      for (const business of businesses.data) {
        // Map the divisions
        let fullDivisions: Division[] = [];

        for (const division of business.divisions) {
          const catalogConfig = catalogConfigData.data.find((item) => item.division?.id === division.id);

          // If full division is found
          if (catalogConfig) {
            if (!inheritanceTypeFilter.length) {
              // If no filters return the division
              fullDivisions = [...business.divisions];
            } else {
              // If filters exists
              // If business type
              if (catalogConfig.dataInheritanceLevel === 'Business' && inheritanceTypeFilter.includes('Business')) {
                const updatedDivision = { ...division, ...{ productCatalogConfiguration: { dataInheritanceLevel: catalogConfig.dataInheritanceLevel } } };

                fullDivisions = [...fullDivisions, ...[updatedDivision]];
              }
              // if division type
              if (catalogConfig.dataInheritanceLevel === 'Division' && inheritanceTypeFilter.includes('Division')) {
                const updatedDivision = { ...division, ...{ productCatalogConfiguration: { dataInheritanceLevel: catalogConfig.dataInheritanceLevel } } };

                fullDivisions = [...fullDivisions, ...[updatedDivision]];
              }
              // if override type
              if (catalogConfig.dataInheritanceLevel === 'BusinessWithDivisionOverride' && inheritanceTypeFilter.includes('BusinessWithDivisionOverride')) {
                const updatedDivision = { ...division, ...{ productCatalogConfiguration: { dataInheritanceLevel: catalogConfig.dataInheritanceLevel } } };

                fullDivisions = [...fullDivisions, ...[updatedDivision]];
              }
            }
          } else {
            fullDivisions = [...fullDivisions, ...[division]];
          }
        }

        // Add business filtered for customer search
        returnData = [...returnData, { ...business, divisions: fullDivisions }].map((business) => ({
          ...business,
          divisions: business.divisions.filter((division) => !customerSearch || division.name.toLowerCase().includes(customerSearch) || division.erpId.toLowerCase().includes(customerSearch))
        }));
      }
    }

    // Only return the search values that contain divisions
    const filteredReturnData = customerSearch?.length ? returnData.filter((business) => business.divisions.length) : returnData;

    return filteredReturnData;
  }, [businesses?.data, catalogConfigData?.data, customerSearch, inheritanceTypeFilter]);

  /* ******************** Render ******************** */
  return (
    <List
      loading={{
        indicator: <BlockLoader direction="loader loader--slideUp" />,
        size: 'large',
        spinning: isDocumentLoading,
        wrapperClassName: isDocumentLoading ? 'list-loading' : ''
      }}
      dataSource={filteredData}
      renderItem={(item): JSX.Element => <BusinessListItem business={item} />}
      rowKey={(item): string => item.id}
    />
  );
};
